import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { type ITabItem, toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import type { IResponseVoice, IVoiceType } from 'entities/TextToSpeech'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { NewCustomVoiceStore } from 'widgets/NewCustomVoiceModal'
import { type ITextToSpeechWidgetStoreConfigStore } from 'widgets/TextToSpeech/store/textToSpeechWidgetStore'

export class TextToSpeechVoiceConfigDefault implements ITextToSpeechWidgetStoreConfigStore {
  constructor(
    readonly entities: IEntitiesStore,
    readonly parent: {
      setSelectedVoice: (voice: IResponseVoice) => void
      selectedVoice: IResponseVoice | null
      initVoiceId?: string
    }
  ) {
    makeAutoObservable(this)
  }

  init = async () => {
    let basicVoice: IResponseVoice | null = null

    const setFirstBasicVoice = () => {
      if (this.entities.textToSpeechStore.basicVoices.length) {
        basicVoice = this.entities.textToSpeechStore.basicVoices[0]
      }
      if (basicVoice) {
        this.parent.setSelectedVoice(basicVoice)
      }
    }

    try {
      if (this.parent.initVoiceId) {
        await Promise.all([
          this.entities.textToSpeechStore.loadBasicVoices(),
          this.entities.textToSpeechStore.loadPremiumVoices(),
          this.entities.textToSpeechStore.loadCustomVoices(),
        ])
        const findBasicVoice =
          this.parent.initVoiceId &&
          this.entities.textToSpeechStore.basicVoices.find(
            (item) => item.id === this.parent.initVoiceId
          )
        if (findBasicVoice) {
          this.parent.setSelectedVoice(findBasicVoice)
          this.activeTabKey = 'basic'
          return
        }
        const findPremiumVoice =
          this.parent.initVoiceId &&
          this.entities.textToSpeechStore.premiumVoices.find(
            (item) => item.id === this.parent.initVoiceId
          )
        if (findPremiumVoice) {
          this.parent.setSelectedVoice(findPremiumVoice)
          this.activeTabKey = 'premium'
          return
        }
        const findCustomVoice =
          this.parent.initVoiceId &&
          this.entities.textToSpeechStore.customVoices.find(
            (item) => item.id === this.parent.initVoiceId
          )
        if (findCustomVoice) {
          this.parent.setSelectedVoice(findCustomVoice)
          this.activeTabKey = 'custom'
          return
        }
        setFirstBasicVoice()
      } else {
        await this.entities.textToSpeechStore.loadBasicVoices()
        setFirstBasicVoice()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  search = ''
  setSearch = (value: string) => {
    this.search = value
  }

  activeTabKey: IVoiceType = 'basic'

  private filterVoicesBySearch = (voice: IResponseVoice) =>
    voice.name.toLowerCase().includes(this.search.toLowerCase())

  get basicVoices(): IResponseVoice[] {
    return this.entities.textToSpeechStore.basicVoices.filter(this.filterVoicesBySearch)
  }
  get premiumVoices(): IResponseVoice[] {
    return this.entities.textToSpeechStore.premiumVoices.filter(this.filterVoicesBySearch)
  }
  get customVoices(): IResponseVoice[] {
    return this.entities.textToSpeechStore.customVoices.filter(this.filterVoicesBySearch)
  }
  get isCreateCustomVoice(): boolean {
    return this.activeTabKey === 'custom' && !this.entities.textToSpeechStore.customVoices.length
  }

  private get voicesObj(): { [key in IVoiceType]: IResponseVoice[] } {
    return {
      basic: this.basicVoices,
      premium: this.premiumVoices,
      custom: this.customVoices,
    }
  }

  get voices(): IResponseVoice[] {
    return this.voicesObj[this.activeTabKey]
  }

  get tabs(): ITabItem<IVoiceType>[] {
    return [
      {
        key: 'basic',
        count: this.basicVoices.length,
        name: 'Basic',
      },
      {
        key: 'premium',
        count: this.premiumVoices.length,
        name: 'Premium',
      },
      {
        key: 'custom',
        icon: 'stars',
        iconProps: {
          color: 'var(--orange-60)',
        },
        count: this.customVoices.length,
        name: 'Clone Your Voice™',
      },
    ]
  }

  private setActiveTabKey = (activeTabKey: IVoiceType) => {
    this.activeTabKey = activeTabKey
  }

  handleSelectTab = (tab: ITabItem<IVoiceType>) => {
    this.setActiveTabKey(tab.key)
  }
  onCreateCustomVoice = () => {
    new NewCustomVoiceStore(
      {
        onSuccessCreateVoice: (voice) => {
          this.setActiveTabKey('custom')
          this.parent.setSelectedVoice(voice)
        },
      },
      this.entities
    )
  }
  onDeleteCustomVoice = (voice_id: string) => {
    const modalId = nanoid()
    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.ALERT,
      title: 'Delete custom voice?',
      desc: 'This action cannot be undone',
      primaryAction: {
        text: 'Delete',
        onAction: async () => {
          try {
            await this.entities.textToSpeechStore.deleteCustomVoice(voice_id)
            toastStore.add({
              type: 'success',
              title: 'Custom voice deleted',
            })

            if (this.parent.selectedVoice?.id === voice_id) {
              this.setActiveTabKey('basic')
              this.parent.setSelectedVoice(this.entities.textToSpeechStore.basicVoices[0])
            }
            modalStore.closeModal(modalId)
          } catch (e) {
            logger.error(e)
          }
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(modalId)
        },
      },
    })
  }

  loadVoices = () => {
    this.entities.textToSpeechStore.loadPremiumVoices()
    this.entities.textToSpeechStore.loadCustomVoices()
  }

  getVoicePreview = async (id: string) => {
    return this.entities.textToSpeechStore.getVoicePreview(id, this.activeTabKey)
  }

  onErrorLoadPreview = (id: string) => {
    this.entities.textToSpeechStore.markAsInvalidPreview(id)
  }

  loadAudioSpeech: ITextToSpeechWidgetStoreConfigStore['loadAudioSpeech'] = (params) =>
    this.entities.textToSpeechStore.getSpeechPreview(params)
  getSpeechPreview: ITextToSpeechWidgetStoreConfigStore['getSpeechPreview'] = (key) =>
    this.entities.textToSpeechStore.speechesPreviewMap.get(key)
}
